export interface UserPermission {
	id: string;
	userId: string;
	module: string;
	canCreate: boolean;
	canRead: boolean;
	canUpdate: boolean;
	canDelete: boolean;
	canExport: boolean;
	canShare: boolean;
}

export type PermissionAction = "create" | "read" | "update" | "delete" | "export" | "share";

export const ALL_MODULES = [
	"companies",
	"persons",
	"leads",
	"activities",
	"offers",
	"orders",
	"contracts",
	"payments",
	"warehouses",
	"stock_items",
	"documents",
	"load_reports",
	"deliveries",
	"special_days",
	"sync",
	"admin",
	"settings",
	"ai",
] as const;

export const USER_MODULES = ALL_MODULES.filter(
	(m) => m !== "sync" && m !== "admin" && m !== "settings",
);

export interface DefaultPermissionRow {
	module: string;
	canCreate: boolean;
	canRead: boolean;
	canUpdate: boolean;
	canDelete: boolean;
	canExport: boolean;
	canShare: boolean;
}

type DefaultActions = Omit<DefaultPermissionRow, "module">;

const NONE: DefaultActions = {
	canCreate: false,
	canRead: false,
	canUpdate: false,
	canDelete: false,
	canExport: false,
	canShare: false,
};

const READ_ONLY: DefaultActions = { ...NONE, canRead: true };

const CREATE_READ_UPDATE: DefaultActions = {
	...NONE,
	canCreate: true,
	canRead: true,
	canUpdate: true,
};

const READ_UPDATE: DefaultActions = { ...NONE, canRead: true, canUpdate: true };

const FULL: DefaultActions = {
	canCreate: true,
	canRead: true,
	canUpdate: true,
	canDelete: true,
	canExport: true,
	canShare: true,
};

const USER_DEFAULT_ACTIONS: Record<string, DefaultActions> = {
	companies: READ_UPDATE,
	persons: NONE,
	leads: CREATE_READ_UPDATE,
	activities: CREATE_READ_UPDATE,
	offers: CREATE_READ_UPDATE,
	orders: CREATE_READ_UPDATE,
	contracts: CREATE_READ_UPDATE,
	payments: NONE,
	warehouses: READ_ONLY,
	stock_items: READ_ONLY,
	documents: READ_ONLY,
	load_reports: NONE,
	deliveries: NONE,
	special_days: READ_ONLY,
	ai: FULL,
};

export function defaultPermissionsForRole(role: string): DefaultPermissionRow[] {
	if (role === "admin") {
		return ALL_MODULES.map((module) => ({ module, ...FULL }));
	}
	if (role === "user") {
		return USER_MODULES.map((module) => ({
			module,
			...(USER_DEFAULT_ACTIONS[module] ?? NONE),
		}));
	}
	if (role === "customer") {
		return [{ module: "stock_items", ...READ_ONLY }];
	}
	return [];
}

export function hasPermission(
	role: string,
	permissions: UserPermission[],
	module: string,
	action: PermissionAction,
): boolean {
	const actionMap: Record<string, keyof UserPermission> = {
		create: "canCreate",
		read: "canRead",
		update: "canUpdate",
		delete: "canDelete",
		export: "canExport",
		share: "canShare",
	};
	const actionKey = actionMap[action];
	if (!actionKey) return false;
	const perm = permissions.find((p) => p.module === module);
	if (!perm) return false;
	return !!perm[actionKey];
}
